import React, { useCallback, useEffect, useRef, useState } from 'react';
import DefaultModal from 'Douleutaras/components/modals/DefaultModal';
import { ButtonModifiers, CustomButton } from 'Douleutaras/components/CustomButton/CustomButton';
import PostJobForm from 'Douleutaras/views/job/post-job/PostJobForm';
import Modal from 'Douleutaras/components/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { SITE_ID } from 'Douleutaras/constants/localization';
import logoYourHero from 'Assets/layout/job/icon-logo-yourhero-round.svg';
import logoDouleutaras from 'Assets/layout/job/icon-logo-douleutaras-round.svg';
import { useLocalization } from 'Douleutaras/hooks/localization';
import { useAppDispatch, useAppSelector } from 'Douleutaras/redux/hooks';
import { categoryFormSelectors } from 'Douleutaras/redux/data/v2/categoryForm/categoryFormSlice';
import { PostJobFormRef, PostJobSteps } from 'Douleutaras/views/job/post-job/hooks/post-job';
import { useSearchParams } from 'react-router-dom';
import Progressbar from 'Douleutaras/components/Progressbar/Progressbar';
import { DecidedInitialValues, decideInitialValues } from 'Douleutaras/views/job/post-job/helpers/post-job-helpers';
import { fetchPostingFlow } from 'Douleutaras/redux/data/pros/postingFlowSlice';
import { POST_JOB_FLOW } from 'Douleutaras/views/job/post-job/types';
import { useAB } from 'Douleutaras/app/lib/ab';

import './PostJobModal.scss';
import JobSideInfo from './JobSideInfo';

interface PostJobModalProps {
  autoDealIds?: Array<string>;
  initialCategoryId?: string;
  closeButtonEnabled?: boolean;
  internalFlow?: POST_JOB_FLOW;
  iframe?: boolean;
  initialQuestionsHistory?: Array<number>;
  initialValues?: { questions: {}};
  jumpTo?: PostJobSteps;
  proId?: string;
  proUsername?: string;
  proMugshot?: string;
  sideInfoEnabled?: boolean;
  categoryIconEnabled?: boolean;
  showProAvatar?: boolean;
  backButtonCallback?(): void;
  closeButtonCallback?(): void;
}

// Get category id by priority
const getCategoryId = (initialCategoryId, searchParams, postJobRef) => {
  if (initialCategoryId) {
    return initialCategoryId;
  }

  if (searchParams.has('category') && !isNaN(parseInt(searchParams.get('category'), 0))) {
    return searchParams.get('category');
  }

  return postJobRef.current?.categoryId;
};

export const PostJobModal = ({
                               autoDealIds,
                               backButtonCallback,
                               categoryIconEnabled = true,
                               closeButtonCallback,
                               closeButtonEnabled = true,
                               iframe = false,
                               initialCategoryId,
                               internalFlow,
                               jumpTo,
                               proId,
                               proUsername,
                               proMugshot,
                               sideInfoEnabled = false,
                               showProAvatar = false
}: PostJobModalProps) => {
  const { t } = useTranslation();
  const { siteId } = useLocalization();
  const postJobRef = useRef<PostJobFormRef>(undefined);

  const [ nextButtonEnabled, setNextButtonEnabled ] = useState<boolean>();
  const [ backButtonEnabled, setBackButtonEnabled ] = useState<boolean>(false);
  const [ nextButtonText, setNextButtonText ] = useState<string | React.ReactElement>();
  // Set while the location step shows its inline map picker — swaps the action button for Confirm.
  const [ locationMapView, setLocationMapView ] = useState<{ confirmDisabled: boolean }>();
  const [ isFormValid, setIsFormValid ] = useState<boolean>();
  const [ isFormSubmitting, setIsFormSubmitting ] = useState<boolean>(false);
  const [ searchParams ] = useSearchParams();
  const [ progressTranslateX, setProgressTranslateX ] = useState<string>('-100%');
  const defaultLogo = siteId === SITE_ID.IE ? logoYourHero : logoDouleutaras;
  const dispatch = useAppDispatch();
  const categoryId = getCategoryId(initialCategoryId, searchParams, postJobRef);
  const categoryForm = useAppSelector(state => categoryFormSelectors.selectById(state, categoryId));
  const postingFlow = useAppSelector(state => state.pros.postingFlow);
  const ab = useAB();

  // Send event messages on modal Open / Close (used in mobile app webview)
  useEffect(() => {
    // @AB-Service
    ab.captureJobFormOpened();

    window.postMessage('jobModal:open', window.location.origin);

    // Add iOS height hack when modal opens
    document.body.style.height = '100vh';
    document.body.style.height = '100dvh';

    return () => {
      window.postMessage('jobModal:close', window.location.origin);

      // Remove iOS height hack when modal closes
      document.body.style.removeProperty('height');
    };
  }, []);

  const shouldFetchPostingFlow = Boolean(categoryId);

  const stepChangeCallback = useCallback(questions => {
    // postingFlow API call disabled if no sideInfo is enabled (rebooking) or category is missing
    if (!shouldFetchPostingFlow) {
      return;
    }

    // Enable fetching additional info if sideInfoEnabled is enabled
    dispatch(fetchPostingFlow({ categoryId, questions }));
  }, [shouldFetchPostingFlow, dispatch, categoryId]);

  const progressBarUpdate = useCallback(payload => {
    const translate = payload?.progressTranslateX;

    if (typeof translate === 'string') {
      setProgressTranslateX(translate);
    }

  }, []);

  const initialValues: DecidedInitialValues = decideInitialValues(searchParams, categoryId);
  const postJobFlow = internalFlow === POST_JOB_FLOW.DIRECT_CONTACT
    ? POST_JOB_FLOW.DIRECT_CONTACT
    : POST_JOB_FLOW.LEGACY;
  const nextButtonDisabled = !isFormValid || isFormSubmitting || (shouldFetchPostingFlow && !postingFlow.loaded);

  return <Modal isOpen dismissCallback={() => true} iframe={iframe}>
    <DefaultModal
      sideInfo={
          sideInfoEnabled &&
            <>
              <JobSideInfo categoryId={postJobRef.current?.categoryId || categoryId} />
              <div styleName="progress">
                <Progressbar value={progressTranslateX} />
              </div>
            </>
      }
      userMugshot={showProAvatar ? (proMugshot || defaultLogo) : undefined}
      serviceIcon={categoryIconEnabled ? categoryForm?.category?.category_group.image : undefined}
      showBackButton={backButtonEnabled && !locationMapView}
      fullWidth={Boolean(locationMapView)}
      backText={t('T.POST-JOB.Go Back')}
      closeButtonCallback={
        locationMapView
          ? () => postJobRef.current.closeLocationMap()
          : (closeButtonEnabled ? closeButtonCallback : undefined)
      }
      backButtonCallback={() => {
        if (typeof backButtonCallback === 'function') {
          backButtonCallback();

          return;
        } else if (typeof postJobRef?.current?.questionsRef?.current?.switchStepBack === 'function') {
          postJobRef.current.questionsRef.current.switchStepBack();
        } else {
          postJobRef.current.stepBack();
        }
      }}
      footer={
      locationMapView
        ? <div>
            <CustomButton title={t('T.POST-JOB.MAP.CONFIRM-AREA', 'Confirm area')}
                          modifiers={ButtonModifiers.ROUND}
                          onClick={() => postJobRef.current.confirmLocationMap()}
                          disabled={locationMapView.confirmDisabled}
            />
          </div>
        : <>
        {/* tslint:disable-next-line:max-line-length */}
        {internalFlow === POST_JOB_FLOW.MULTI_BID && postingFlow.loaded &&
          <MultiBidAvatars selectedPros={location?.state?.multiSelectPros} max={postingFlow.max_bids} />}
          {Boolean(nextButtonEnabled)
            ? <div>
                <CustomButton title={nextButtonText}
                              modifiers={ButtonModifiers.ROUND}
                              onClick={() => {
                                if (typeof postJobRef?.current?.questionsRef?.current?.switchToNextStep === 'function') {
                                  postJobRef.current.questionsRef.current.switchToNextStep();
                                } else {
                                  postJobRef.current.goToNextStep();
                                }
                              }}
                              disabled={nextButtonDisabled}
                />
            </div>
            : undefined}
      </>
      }
    >
      <PostJobForm
        iframe={iframe}
        autoDealIds={autoDealIds}
        categoryId={categoryId}
        ref={postJobRef}
        setNextButtonEnabled={setNextButtonEnabled}
        setNextButtonText={setNextButtonText}
        setBackButtonEnabled={setBackButtonEnabled}
        setLocationMapView={setLocationMapView}
        setIsFormValid={setIsFormValid}
        setIsFormSubmitting={setIsFormSubmitting}
        stepChangeCallback={stepChangeCallback}
        progressBarUpdate={progressBarUpdate}
        isFormValid={isFormValid}
        isFormSubmitting={isFormSubmitting}
        initialValues={initialValues.form}
        initialQuestionsHistory={initialValues.initialQuestionsHistory}
        jumpTo={jumpTo ?? initialValues.lastStoredStep}
        proId={proId}
        proUsername={proUsername}
        postJobFlow={postJobFlow}
      />
    </DefaultModal>
  </Modal>;
};

export default PostJobModal;
